// src/pages/PatientProfilePage.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PatientPersonalDetails from "../components/PatientPersonalDetails";
import PatientInterests from "../components/PatientInterests";
import PatientFileCard from "../components/PatientFileCard";
import PatientRadioPicker from "../components/PatientRadioPicker";
import PatientCallButton from "../components/patientCall/PatientCallButton";
import DevicePowerButton from "../components/patientCall/DevicePowerButton";
import PatientReminders from "./PatientReminders";
import FamilyInviteCard from "../components/family/FamilyInviteCard";
import PatientDocumentationPanel from "../components/patientDocumentation/PatientDocumentationPanel";
import DeviceSettingsSection from "../components/deviceSettings/DeviceSettingsSection";

const API = process.env.REACT_APP_API_URL;
const PATIENT_PROFILE_TABS = ["profil", "erinnerungen", "abfragen", "uebungen", "dokumentation"];

function Tabs({ value, onChange }) {
  const items = [
    { key: "profil", label: "Profil" },
    { key: "erinnerungen", label: "Erinnerungen" },
    { key: "abfragen", label: "Abfragen" },
    { key: "uebungen", label: "Aktivierung" },
    { key: "dokumentation", label: "Dokumentation", beta: true },
  ];
  return (
    <div className="mt-2 flex flex-wrap gap-x-8 gap-y-2 border-b border-blue-200">
      {items.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={[
            "pb-2 text-lg inline-flex items-center gap-2",
            value === t.key
              ? "font-semibold text-blue-700 border-b-2 border-blue-600"
              : "text-gray-700 hover:text-gray-900",
          ].join(" ")}
        >
          {t.label}
          {t.beta && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
              Beta
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export default function PatientProfilePage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = new URLSearchParams(location.search).get("tab");

  const [tab, setTab] = useState(PATIENT_PROFILE_TABS.includes(initialTab) ? initialTab : "profil");
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState({
    forename: "",
    surname: "",
    birthdate: "",
    betreuer: "",
    language: "de",
    voice: "female",
    interests: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const hasChanges = patient && (
    ["forename", "surname", "birthdate", "betreuer", "language", "voice"].some(
      (key) => String(form[key] ?? "") !== String(patient[key] ?? (key === "language" ? "de" : key === "voice" ? "female" : ""))
    )
  );

  useEffect(() => {
    const nextTab = new URLSearchParams(location.search).get("tab");
    if (PATIENT_PROFILE_TABS.includes(nextTab)) {
      setTab(nextTab);
    }
  }, [location.search]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        setLoading(true);
        setErr("");
        const res = await fetch(`${API}/patients/${id}`, { credentials: "include" });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!ignore) {
          setPatient(data);
          setForm({
            forename: data.forename ?? "",
            surname: data.surname ?? "",
            birthdate: data.birthdate ?? "",
            betreuer: data.betreuer == null ? "" : String(data.betreuer),
            language: data.language ?? "de",
            voice: data.voice ?? "female",
          });
        }
      } catch (e) {
        if (!ignore) setErr(e?.message || "Fehler beim Laden des Bewohners");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [id]);

  const fullName = patient ? `${patient.forename} ${patient.surname}` : "";
  function updateField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function resetForm() {
    if (!patient) return;
    setForm({
      forename: patient.forename ?? "",
      surname: patient.surname ?? "",
      birthdate: patient.birthdate ?? "",
      betreuer: patient.betreuer == null ? "" : String(patient.betreuer),
      language: patient.language ?? "de",
      voice: patient.voice ?? "female",
    });
  }

  function validate() {
    if (!form.forename.trim() || !form.surname.trim()) {
      setErr("Vorname und Nachname dürfen nicht leer sein.");
      return false;
    }
    if (form.birthdate && !/^\d{4}-\d{2}-\d{2}$/.test(form.birthdate)) {
      setErr("Bitte ein gültiges Geburtsdatum eingeben.");
      return false;
    }
    return true;
  }

  async function handleSave(channelsOverride) {
    if (saving) return;
    const radioOnly = channelsOverride !== undefined;
    if (!radioOnly && !validate()) return;
    setSaving(true);
    setErr("");
    setOkMsg("");
    try {
      const body = radioOnly ? { radio: channelsOverride } : {
        forename: form.forename.trim(),
        surname: form.surname.trim(),
        birthdate: form.birthdate || null,
        betreuer: (form.betreuer ?? "").trim(),
        language: form.language,
        voice: form.voice,
      };
      const res = await fetch(`${API}/patients/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      setPatient(updated);
      setOkMsg("Änderungen gespeichert.");
      if (!radioOnly) setForm({
        forename: updated.forename ?? "",
        surname: updated.surname ?? "",
        birthdate: updated.birthdate ?? "",
        betreuer: updated.betreuer == null ? "" : String(updated.betreuer),
        language: updated.language ?? "de",
        voice: updated.voice ?? "female",
      });
    } catch (e) {
      setErr(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    resetForm();
    setErr("");
    setOkMsg("");
  }

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <Sidebar
        active="alle-bewohner"
        user={{ initials: "E", name: "Erik Rubinov", email: "erik.rubinov@gmail.com" }}
      />

      <main className="flex-1 px-0 pt-6 pb-0">
        <div className="w-full h-full rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="rounded-md px-3 py-1.5 text-sm text-gray-700 hover:bg-white/70"
              >
                ← Zurück
              </button>
              <h1 className="text-3xl font-semibold text-gray-900">
                {loading ? "Lade…" : err ? "Fehler" : fullName}
              </h1>
            </div>
            {!loading && !err && patient && (
              <div className="flex items-start gap-2">
                <DevicePowerButton
                  patientId={id}
                  isDeviceOnline={patient.is_device_online !== false}
                />
                <PatientCallButton patientId={id} isDeviceOnline={patient.is_device_online !== false} />
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={tab} onChange={setTab} />

          {/* status messages */}
          {loading && <div className="mt-6 text-sm text-gray-700">Daten werden geladen…</div>}
          {err && !loading && (
            <div className="mt-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {err}
            </div>
          )}
          {okMsg && !loading && !err && (
            <div className="mt-4 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              {okMsg}
            </div>
          )}

          {/* Content */}
          {!loading && !err && patient && tab === "profil" && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-6">
              {/* Left column */}
              <div className="space-y-6">
                <PatientPersonalDetails
                  hasChanges={hasChanges}
                  saving={saving}
                  onSave={() => handleSave()}
                  onCancel={handleCancel}
                  form={form}
                  onChangeField={updateField}
                />

                {/* Interessen */}
                <PatientInterests
                  patientId={id}
                  initialInterests={patient.interests || []}
                  onSaved={(ints) => {
                    setPatient((p) => (p ? { ...p, interests: ints } : p));
                    setForm((f) => ({ ...f, interests: ints }));
                  }}
                />

                {/* Medienangebote */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Medienangebote</h2>
                  <div className="h-px w-full bg-gray-300/70 mb-3" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {patient.radio_stations === null ? (
                      <p>Radiosender sind vorübergehend nicht verfügbar.</p>
                    ) : (
                      <PatientRadioPicker
                        initialChannels={patient.radio_stations.map((station) => station.id)}
                        max={5}
                        onSaved={(channels) => handleSave(channels)}
                      />
                    )}
                  </div>
                </section>

                <DeviceSettingsSection patientId={id} />
              </div>

              {/* Right column */}
              <div className="space-y-6">
                <PatientFileCard
                  patientId={id}
                  filePath={patient?.bio_path || null}
                  onChangeFilePath={(newPath) => {
                    setPatient((p) => (p ? { ...p, bio_path: newPath } : p));
                  }}
                />
                <FamilyInviteCard patientId={id} />
              </div>
            </div>
          )}

          {!loading && !err && patient && tab === "erinnerungen" && (
            
              <PatientReminders patientId={id} templateType="reminder" />
          )}

          {!loading && !err && patient && tab === "abfragen" && (
            <PatientReminders patientId={id} templateType="query" />
          )}

          {!loading && !err && patient && tab === "uebungen" && (
            <PatientReminders patientId={id} templateType="activation" />
          )}

          {!loading && !err && patient && tab === "dokumentation" && (
            <PatientDocumentationPanel patientId={id} />
          )}
        </div>
      </main>
    </div>
  );
}
